import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  TextField,
} from "@mui/material";
import PropTypes from "prop-types";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import TOASTER from "../../config/toaster";
import { COMMON_MSG } from "../../common/messages";
import logger from "../../helpers/logger";
import DraggableDialog from "../../common/components/DraggableDialog";
import { addTimeZoneData, updateTimeZoneData } from "../../api/timesetup-api-service";
import FormError from "../../common/components/FormError";
import { useState } from "react";

const TimeZoneDialog = (props) => {
  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting, isDirty },
  } = useForm({
    defaultValues: props.formData,
    mode: "all",
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const handleCancel = () => {
    if (isDirty) {
      setHasUnsavedChanges(true);
    } else {
      props.onClose();
    }
  };

  const handleCrudOperation = async (data) => {
    let toastId = toast.loading(COMMON_MSG.Processing);

    try {
      let response;

      if (data?.timeZone_Key) {
        response = await updateTimeZoneData(
          data?.timeZone_Key,
          data,
          toastId
        );
      } else {
        response = await addTimeZoneData(data, toastId);
      }

      if (response.isSuccess) {
        await props.onSuccess();
        toast.update(toastId, {
          render: response.message,
          type: "success",
          isLoading: false,
          autoClose: TOASTER.AUTO_CLOSE,
        });
      }

      props.onClose();
    } catch (error) {
      logger.log(error);
    }
  };

  return (
    <DraggableDialog
      onClose={props.onClose}
      title={props?.isEditing ? "Edit Time Zone" : "Add Time Zone"}
      maxWidth="sm"
      fullWidth
      open={props.open}
      hasUnsavedChanges={isDirty}
      showUnsavedConfirmCancel={hasUnsavedChanges}
      onUnsavedConfirmCancel={() => setHasUnsavedChanges(false)}
    >
      <DialogContent>
        <FormControl fullWidth margin="normal">
          <Controller
            name="timeZone_Name"
            control={control}
            rules={{
              required: true,
              minLength: {
                value: 3,
              },
              validate: {
                alphaOnly: (value) =>
                  /^[A-Za-z\s]+$/.test(value)
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Name"
                fullWidth
                required
                margin="normal"
                error={!!errors.timeZone_Name}
              />
            )}
          />
          <FormError errors={errors} controlId="timeZone_Name" />
        </FormControl>

        <FormControl fullWidth margin="normal">
          <Controller
            name="timeZone_IANA_Name"
            control={control}
            rules={{
              required: true,
              pattern: {
                value: /^[A-Za-z_]+\/[A-Za-z_]+$/,
                message:
                  "Must be a valid IANA name like 'America/New_York'",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="IANA Name"
                fullWidth
                required
                margin="normal"
                error={!!errors.timeZone_IANA_Name}
              />
            )}
          />
          <FormError errors={errors} controlId="timeZone_IANA_Name" />
        </FormControl>

      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} sx={{ color: "gray" }}>
          Cancel
        </Button>
        <Button
          sx={{ marginRight: "15px" }}
          variant="contained"
          color="primary"
          onClick={handleSubmit(handleCrudOperation)}
          disabled={!isValid || isSubmitting}
        >
          Save
        </Button>
      </DialogActions>
    </DraggableDialog>
  );
};

TimeZoneDialog.propTypes = {
  open: PropTypes.bool,
  formData: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
};

export default TimeZoneDialog;